import { Box, Typography } from "@mui/material"
import type { FC } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import type { GlTransactionFormValues } from "../schemas/glTransactionForm"
import { buildRecurringSummary } from "../utils/glTransactionFormUtils"

import { FormDatePicker } from "@/components/UiKit/FormControls/FormDatePicker"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { GlTransactionRepeatByUnit } from "@/graphql/codegen/graphql"

const REPEAT_BY_UNIT_OPTIONS = [
  { value: GlTransactionRepeatByUnit.Week, label: "Week(s)" },
  { value: GlTransactionRepeatByUnit.Month, label: "Month(s)" },
  { value: GlTransactionRepeatByUnit.Year, label: "Year(s)" },
]

export const RecurringSection: FC = () => {
  const { control } = useFormContext<GlTransactionFormValues>()
  const transactionDate = useWatch({ control, name: "transactionDate" })
  const repeatEndDate = useWatch({ control, name: "repeatEndDate" })
  const repeatEvery = useWatch({ control, name: "repeatEvery" })
  const repeatByUnit = useWatch({ control, name: "repeatByUnit" })

  const showSummary = transactionDate && repeatEndDate && repeatEvery && repeatByUnit && repeatEndDate > transactionDate

  const summary = showSummary ? buildRecurringSummary(transactionDate, repeatEndDate, repeatEvery, repeatByUnit) : null

  return (
    <Box className='flex flex-col gap-4'>
      <Box className='grid grid-cols-2 items-start gap-4'>
        <Box className='flex flex-col gap-1'>
          <Typography variant='body2' className='text-gray-700'>
            Repeat every
          </Typography>
          <Box className='flex gap-2'>
            <FormNumericField
              control={control}
              name='repeatEvery'
              label=''
              decimalScale={0}
              allowNegative={false}
              className='w-24'
              data-testid='gl-transaction.form-dialog.repeat-every'
            />
            <FormSelect
              control={control}
              name='repeatByUnit'
              label=''
              options={REPEAT_BY_UNIT_OPTIONS}
              data-testid='gl-transaction.form-dialog.repeat-by-unit'
            />
          </Box>
        </Box>

        <FormDatePicker
          control={control}
          name='repeatEndDate'
          label='Transaction End Date'
          required
          data-testid='gl-transaction.form-dialog.recurring-end-date'
        />
      </Box>

      {summary && (
        <Typography variant='body2' className='text-primary'>
          {summary}
        </Typography>
      )}
    </Box>
  )
}
